import { useRef, useState, useEffect } from "react";
import { api, BACKEND_URL } from "@/lib/api";

const BEFORE_DEFAULT = "https://images.unsplash.com/photo-1612817288484-6f916006741a?auto=format&fit=crop&w=1200&q=80";
const AFTER_DEFAULT = "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=1200&q=80";

const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  return `${BACKEND_URL}${u}`;
};

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const [beforeSrc, setBeforeSrc] = useState(BEFORE_DEFAULT);
  const [afterSrc, setAfterSrc] = useState(AFTER_DEFAULT);
  const ref = useRef(null);
  const dragging = useRef(false);

  useEffect(() => {
    api
      .get("/site/hero")
      .then((r) => {
        const d = r.data || {};
        if (d.before_after_before_image) setBeforeSrc(absoluteUrl(d.before_after_before_image));
        if (d.before_after_after_image) setAfterSrc(absoluteUrl(d.before_after_after_image));
      })
      .catch(() => {});
  }, []);

  const move = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    setPos((x / rect.width) * 100);
  };

  useEffect(() => {
    const onMove = (e) => dragging.current && move(e.clientX);
    const onTouch = (e) => dragging.current && move(e.touches[0].clientX);
    const onUp = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onTouch);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  return (
    <section className="py-24 md:py-32 bg-[#F8F5F0]" data-testid="before-after-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-4 mb-4">
            <span className="divider-gold" />
            <span className="overline">Real Results</span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tighter font-light leading-[1.05]">
            Before. After.<br />
            <em className="gold-text not-italic">Always Blisson.</em>
          </h2>
          <p className="text-base text-[#3a3a3a] mt-6 max-w-md leading-relaxed">
            Visible transformations, captured on real clients using our 28-day Radiance Ritual. No filters, no retouching — just formulations that perform.
          </p>
          <div className="grid grid-cols-3 gap-6 mt-10 max-w-md">
            {[
              { v: "28", l: "Day Ritual" },
              { v: "92%", l: "See Glow" },
              { v: "100%", l: "Real Clients" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-serif-display text-3xl">{s.v}</div>
                <div className="overline mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div
            ref={ref}
            className="relative aspect-[4/5] md:aspect-[5/4] w-full overflow-hidden select-none cursor-ew-resize luxury-shadow"
            onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
            onTouchStart={(e) => { dragging.current = true; move(e.touches[0].clientX); }}
            data-testid="before-after-slider"
          >
            <img src={afterSrc} alt="After — silk-smooth restored hair" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${pos}%` }}
            >
              <img
                src={beforeSrc}
                alt="Before — dull, frizzy strands"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full object-cover"
                style={{ width: ref.current ? `${ref.current.offsetWidth}px` : "100%", maxWidth: "none" }}
                draggable={false}
              />
            </div>
            <div className="absolute top-4 left-4 text-[10px] tracking-[0.3em] uppercase bg-white/85 backdrop-blur px-3 py-1">Before</div>
            <div className="absolute top-4 right-4 text-[10px] tracking-[0.3em] uppercase bg-black text-white px-3 py-1">After</div>
            <div
              className="absolute top-0 bottom-0 w-[2px] bg-[#C9A961]"
              style={{ left: `calc(${pos}% - 1px)` }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border border-[#C9A961] flex items-center justify-center shadow-lg">
                <span className="text-[#C9A961] text-sm font-medium">↔</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
